// Assignments → Notifications: an Admin's editor for someone else's Custom
// Notification (owner, 2026-11-02 — docs/decisions.md D-454). "+" opens it to
// add one, and a Custom row opens it to edit, switch off or remove one.
//
// THE SAME FORM as the header's Custom Notifications pop-up
// (`CustomNotificationForm`), with an Assigned To field above the Type. The steps it
// offers are THAT PERSON'S — the server reads them from their roles and seats
// (§14.5) — never the Admin's, because the step check takes off any step the
// owner does not hold (D-392). Until a person is picked there are none to offer.
//
// The person owns it once it exists: it shows in their own pop-up, and they may
// edit, switch off or remove it there (owner, the same day).
import { useEffect, useState } from 'react'
import {
  Button,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Text,
} from '@fluentui/react-components'
import { DismissRegular } from '@fluentui/react-icons'
import { AppDialog } from '../components/AppDialog'
import { CustomNotificationForm } from '../components/CustomNotificationForm'
import { DecisionDialog } from '../components/DecisionDialog'
import { EntityPicker, type EntityOption } from '../components/EntityPicker'
import { errorMessage } from '../api/errors'
import { loadSelectableSteps, searchPeople } from '../lib/data'
import {
  cancelSubscription,
  emptyForm,
  isActiveSubscription,
  rowToForm,
  saveEditedSubscription,
  saveNewSubscriptionFor,
  stepOptionsFromKeys,
  validateForm,
  type ReminderForm,
  type StepOption,
  type Subscriber,
} from '../lib/reminders'
import type { DirectoryUser, ReminderSubscription } from '../lib/types'

export function CustomNotificationAdminDialog({
  open,
  subscription,
  onClose,
  onChanged,
}: {
  open: boolean
  /** The notification being edited; null adds a new one. */
  subscription: ReminderSubscription | null
  onClose: () => void
  onChanged: (message: string) => void
}) {
  const [dialogSurface, setDialogSurface] = useState<HTMLDivElement | null>(null)
  const [person, setPerson] = useState<Subscriber | null>(null)
  const [found, setFound] = useState<DirectoryUser[]>([])
  const [form, setForm] = useState<ReminderForm>(() => emptyForm())
  const [active, setActive] = useState(true)
  const [steps, setSteps] = useState<StepOption[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)

  /** Read the person's steps, and keep only those of the picked ones they hold. */
  const loadSteps = async (subscriberId: string, keep: ReminderForm) => {
    setSteps(null)
    try {
      const options = stepOptionsFromKeys(await loadSelectableSteps(subscriberId))
      setSteps(options)
      setForm({ ...keep, steps: keep.steps.filter((key) => options.some((o) => o.key === key)) })
    } catch (e) {
      setSteps([])
      setError(errorMessage(e))
    }
  }

  useEffect(() => {
    if (!open) return
    setError(null)
    setSaving(false)
    setRemoving(false)
    if (subscription === null) {
      setPerson(null)
      setForm(emptyForm())
      setActive(true)
      setSteps([])
      return
    }
    const loaded = rowToForm(subscription)
    setPerson({ id: subscription.subscriber_id, name: subscription.subscriber_name, email: subscription.subscriber_email })
    setForm(loaded)
    setActive(isActiveSubscription(subscription))
    void loadSteps(subscription.subscriber_id, loaded)
  }, [open, subscription])

  const search = (term: string) =>
    searchPeople(term).then<EntityOption[]>((users) => {
      setFound(users)
      return users.map((u) => ({
        id: u.id,
        primary: u.display_name || u.upn,
        secondary: [u.job_title, u.department].filter(Boolean).join(' · ') || u.upn,
      }))
    })

  const pick = (option: EntityOption) => {
    const user = found.find((u) => u.id === option.id)
    if (user === undefined) return
    setError(null)
    setPerson({ id: user.id, name: user.display_name || null, email: user.mail ?? user.upn })
    void loadSteps(user.id, form)
  }

  const who = person === null ? '' : person.name || person.email

  const save = async () => {
    if (person === null) {
      setError('Choose a person.')
      return
    }
    const invalid = validateForm(form)
    if (invalid) {
      setError(invalid)
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (subscription === null) await saveNewSubscriptionFor(form, person)
      else await saveEditedSubscription(subscription, form, active)
      onClose()
      onChanged(
        subscription === null
          ? `The custom notification for ${who} was created.`
          : `The custom notification for ${who} was updated.`,
      )
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (subscription === null) return
    setSaving(true)
    try {
      await cancelSubscription(subscription)
      setRemoving(false)
      onClose()
      onChanged(`The custom notification for ${who} was removed.`)
    } catch (e) {
      setRemoving(false)
      setError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface ref={setDialogSurface} style={removing ? { visibility: 'hidden' } : undefined}>
        <DialogBody>
          <DialogTitle
            action={<Button appearance="subtle" icon={<DismissRegular />} aria-label="Close" onClick={onClose} />}
          >
            {subscription === null ? 'Add Custom Notification' : 'Edit Custom Notification'}
          </DialogTitle>
          <DialogContent>
            <CustomNotificationForm
              form={form}
              onChange={setForm}
              stepOptions={steps ?? []}
              noSteps={
                person === null
                  ? 'Choose a person to see the steps they can act on.'
                  : steps === null
                    ? 'Loading steps…'
                    : `${who} has no steps they can act on.`
              }
              triggeredLabel="Notify them when an invoice arrives at"
              dialogHost={dialogSurface}
              editing={subscription !== null}
              active={active}
              onActiveChange={setActive}
              saving={saving}
              error={error}
              onCancel={onClose}
              onSave={() => void save()}
              lead={
                subscription === null ? (
                  // ASSIGNED TO, as the table's column reads (owner, 2026-11-03; Person until then).
                  <Field label="Assigned To" required>
                    <EntityPicker
                      ariaLabel="Assigned To"
                      value={person === null ? undefined : { id: person.id, name: who }}
                      placeholder="Search by name"
                      mountNode={dialogSurface}
                      search={search}
                      onSelect={pick}
                      disabled={saving}
                    />
                  </Field>
                ) : (
                  // The person is fixed once it exists: it is theirs.
                  <Field label="Assigned To">
                    <Text>{who}</Text>
                  </Field>
                )
              }
              extraActions={
                subscription !== null && (
                  <Button appearance="secondary" disabled={saving} onClick={() => setRemoving(true)}>
                    Remove
                  </Button>
                )
              }
            />
          </DialogContent>
        </DialogBody>
        <DecisionDialog
          open={removing}
          title="Are you sure?"
          body={`The custom notification “${subscription?.title || 'Custom Notification'}” for ${who} will be removed, and its emails will stop.`}
          requireReason={false}
          confirmLabel="Remove"
          submitting={saving}
          onCancel={() => setRemoving(false)}
          onConfirm={() => void remove()}
        />
      </DialogSurface>
    </AppDialog>
  )
}
